package org.zq.astrock_ai.network

import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.header
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.contentType
import org.zq.astrock_ai.api.dto.KlineResponseDto
import org.zq.astrock_ai.api.dto.LicenseGenerateRequestDto
import org.zq.astrock_ai.api.dto.LicenseGenerateResponseDto
import org.zq.astrock_ai.api.dto.LicenseRedeemRequestDto
import org.zq.astrock_ai.api.dto.LicenseRedeemResponseDto
import org.zq.astrock_ai.api.dto.LicenseStatusDto
import org.zq.astrock_ai.api.dto.AuthMeResponseDto
import org.zq.astrock_ai.api.dto.MarketCenterResponseDto
import org.zq.astrock_ai.api.dto.QuantPlanResponseDto
import org.zq.astrock_ai.api.dto.QuantScanResponseDto
import org.zq.astrock_ai.api.dto.RecommendJobStartDto
import org.zq.astrock_ai.api.dto.RecommendProgressDto
import org.zq.astrock_ai.api.dto.RecommendResponseDto
import org.zq.astrock_ai.api.dto.SmsLoginRequestDto
import org.zq.astrock_ai.api.dto.SmsLoginResponseDto
import org.zq.astrock_ai.api.dto.SmsSendRequestDto
import org.zq.astrock_ai.api.dto.SmsSendResponseDto
import org.zq.astrock_ai.api.dto.StockQuoteDto
import org.zq.astrock_ai.api.dto.SupportContactListDto
import org.zq.astrock_ai.api.dto.TickerResponseDto
import org.zq.astrock_ai.quant.StrategyInfo
import org.zq.astrock_ai.ui.research.gushi.data.AuthSessionStore
import org.zq.astrock_ai.ui.research.gushi.data.DeviceLicenseStore

class MarketApiClient(
    private val client: HttpClient = createMarketHttpClient(),
    private val baseUrl: String = apiBaseUrl(),
) {
    suspend fun fetchTicker(): TickerResponseDto =
        client.get("$baseUrl/api/ticker").body()

    suspend fun fetchMarketCenter(): MarketCenterResponseDto =
        client.get("$baseUrl/api/market").body()

    suspend fun fetchWatchlist(codes: List<String>): List<StockQuoteDto> {
        val joined = codes.joinToString(",")
        return client.get("$baseUrl/api/watchlist?codes=$joined").body()
    }

    suspend fun fetchStock(code: String): StockQuoteDto =
        client.get("$baseUrl/api/stock/$code").body()

    suspend fun fetchKline(
        code: String,
        limit: Int = 120,
        period: String = "day",
    ): KlineResponseDto =
        client.get("$baseUrl/api/stock/$code/kline?limit=$limit&period=$period").body()

    suspend fun fetchQuant(
        code: String,
        strategyId: String = "dual_ma",
        lookbackDays: Int = 90,
        costPrice: Double? = null,
        quantity: Int? = null,
        params: Map<String, String> = emptyMap(),
    ): QuantPlanResponseDto {
        val extras = buildString {
            if (costPrice != null) append("&costPrice=$costPrice")
            if (quantity != null) append("&quantity=$quantity")
            if (params.isNotEmpty()) {
                val encoded = params.entries.joinToString(",") { "${it.key}:${it.value}" }
                append("&params=$encoded")
            }
        }
        return client.get(
            "$baseUrl/api/stock/$code/quant?strategyId=$strategyId&lookbackDays=$lookbackDays$extras",
        ).body()
    }

    suspend fun fetchQuantStrategies(): List<StrategyInfo> =
        client.get("$baseUrl/api/quant/strategies").body()

    suspend fun fetchQuantScan(
        codes: List<String>,
        strategyId: String = "consensus",
        lookbackDays: Int = 90,
    ): QuantScanResponseDto {
        val joined = codes.joinToString(",")
        return client.get(
            "$baseUrl/api/quant/scan?codes=$joined&strategyId=$strategyId&lookbackDays=$lookbackDays",
        ).body()
    }

    suspend fun fetchRecommend(
        limit: Int = 5,
        boards: String = "cyb",
        style: String = org.zq.astrock_ai.api.RecommendStyle.OVERNIGHT,
    ): RecommendResponseDto {
        val b = boards.trim().ifBlank { "cyb" }
        val s = org.zq.astrock_ai.api.RecommendStyle.parse(style)
        // refresh=1 全量拉最新；full=1 单市场全量扫描（耗时更长）
        return client.get(
            "$baseUrl/api/quant/recommend?limit=${limit.coerceIn(1, 5)}&boards=$b&style=$s&refresh=1&full=1",
        ) { licenseHeaders() }.body()
    }

    suspend fun startRecommendJob(
        limit: Int = 5,
        boards: String = "cyb",
        style: String = org.zq.astrock_ai.api.RecommendStyle.OVERNIGHT,
    ): RecommendJobStartDto {
        val b = boards.trim().ifBlank { "cyb" }
        val s = org.zq.astrock_ai.api.RecommendStyle.parse(style)
        return client.get(
            "$baseUrl/api/quant/recommend/start?limit=${limit.coerceIn(1, 5)}&boards=$b&style=$s&refresh=1&full=1",
        ) { licenseHeaders() }.body()
    }

    suspend fun fetchRecommendStatus(jobId: String): RecommendProgressDto =
        client.get("$baseUrl/api/quant/recommend/status?jobId=$jobId") { licenseHeaders() }.body()

    suspend fun fetchLicenseStatus(deviceId: String, token: String): LicenseStatusDto =
        client.get("$baseUrl/api/license/status?deviceId=$deviceId&token=$token") {
            authHeaders()
        }.body()

    suspend fun redeemLicense(code: String, deviceId: String): LicenseRedeemResponseDto =
        client.post("$baseUrl/api/license/redeem") {
            contentType(ContentType.Application.Json)
            setBody(LicenseRedeemRequestDto(code = code, deviceId = deviceId))
        }.body()

    suspend fun generateLicense(plan: String, count: Int, authToken: String): LicenseGenerateResponseDto =
        client.post("$baseUrl/api/license/generate") {
            contentType(ContentType.Application.Json)
            header(io.ktor.http.HttpHeaders.Authorization, "Bearer $authToken")
            setBody(LicenseGenerateRequestDto(plan = plan, count = count))
        }.body()

    suspend fun fetchAuthMe(authToken: String): AuthMeResponseDto =
        client.get("$baseUrl/api/auth/me") {
            header(io.ktor.http.HttpHeaders.Authorization, "Bearer $authToken")
        }.body()

    private fun io.ktor.client.request.HttpRequestBuilder.licenseHeaders() {
        header("X-Device-Id", DeviceLicenseStore.deviceId())
        val token = DeviceLicenseStore.load()?.token.orEmpty()
        if (token.isNotBlank()) header("X-License-Token", token)
        authHeaders()
    }

    private fun io.ktor.client.request.HttpRequestBuilder.authHeaders() {
        val auth = AuthSessionStore.load()?.token.orEmpty()
        if (auth.isNotBlank()) {
            header(io.ktor.http.HttpHeaders.Authorization, "Bearer $auth")
            header("X-Auth-Token", auth)
        }
    }

    suspend fun sendSmsCode(phone: String): SmsSendResponseDto =
        client.post("$baseUrl/api/auth/sms/send") {
            contentType(ContentType.Application.Json)
            setBody(SmsSendRequestDto(phone = phone))
        }.body()

    suspend fun loginWithSms(phone: String, code: String): SmsLoginResponseDto =
        client.post("$baseUrl/api/auth/sms/login") {
            contentType(ContentType.Application.Json)
            setBody(SmsLoginRequestDto(phone = phone, code = code))
        }.body()

    suspend fun fetchSupportContacts(): SupportContactListDto =
        client.get("$baseUrl/api/support/contacts").body()
}
